import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  FreeShipping,
  FreeShippingReqBody,
  PageType,
} from "@/types/coupon/discount";
import React, { useState } from "react";
import SaveDiscountCouponSection from "../saveDiscountCouponSection";
import { createDiscount } from "@/services/discountService";
import { resetFreeShipping } from "@/store/slices/discount.slice";
import { toast } from "sonner";
import { useNavigate, useParams } from "react-router-dom";
import { convertCurrencyToDecimal } from "@/utils/currency";

// UUID validation utility
const isValidUUID = (id?: string) =>
  typeof id === "string" &&
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(
    id,
  );

const SaveFreeShipping = ({ currentPage }: { currentPage: PageType }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { storeType, storeId } = useParams<{ storeType: string; storeId: string }>();
  const [loading, setLoading] = useState(false);
  
  const { couponId, ...rest } = useAppSelector(
    (state) => state.discount[currentPage],
  ) as FreeShipping & { couponId?: string };
  // Destructure all required fields from rest
  const {
    title,
    discount_code,
    discount_type,
    discount_value_percentage,
    discount_value_fixed,
    end_date,
    minimum_purchase_type,
    start_date,
    minimum_purchase_value_amount,
    minimum_purchase_value_quantity,
    discount_value_max,
  } = rest;
  const currentTab = useAppSelector((state) => state.discount.currentTab);

  const handleSave = async () => {
    // Edge case: Ensure title and discountCode are not empty
    const adjustedTitle = title || discount_code;
    const adjustedDiscountCode = discount_code || title;

    const validCouponId = isValidUUID(couponId) ? couponId : undefined;
    const reqBody: FreeShippingReqBody = {
      discountType: discount_type,
      enabled: true,
      automatic: currentTab === "automatic",
      discountCode: adjustedDiscountCode,
      discountValue: Number(
        discount_type === "percentage"
          ? discount_value_percentage
          : convertCurrencyToDecimal(Number(discount_value_fixed)),
      ),
      endDate: end_date ? new Date(end_date).getTime() : null,
      minimumPurchaseType: minimum_purchase_type,
      minimumPurchaseValue: Number(
        minimum_purchase_type === "amount"
          ? convertCurrencyToDecimal(Number(minimum_purchase_value_amount))
          : minimum_purchase_type === "quantity"
            ? minimum_purchase_value_quantity
            : 0,
      ),
      startDate: start_date ? new Date(start_date).getTime() : null,
      title: adjustedTitle,
      ...(Number(discount_value_max) > 0 && {
        discountValueMax: convertCurrencyToDecimal(Number(discount_value_max)),
      }),
      ...(validCouponId && { couponId: validCouponId }),
    };

    if (reqBody.automatic) {
      delete reqBody.discountCode;
    }

    console.group("Save Free Shipping Debugging");
    console.log("Request Body:", reqBody);
    console.log("Current Tab:", currentTab);

    const endpoint = validCouponId
      ? `free-shipping?id=${validCouponId}`
      : `free-shipping`;
    // Log UUID and endpoint
    const vConsole = typeof window !== "undefined" && (window as any).vConsole;
    if (vConsole) {
      vConsole.log("[Coupon Save] UUID:", validCouponId);
      vConsole.log("[Coupon Save] Endpoint:", endpoint);
      vConsole.log("[Coupon Save] Request Body:", reqBody);
    } else {
      console.log("[Coupon Save] UUID:", validCouponId);
      console.log("[Coupon Save] Endpoint:", endpoint);
      console.log("[Coupon Save] Request Body:", reqBody);
    }

    try {
      setLoading(true);

      // Validate request body fields
      if (
        reqBody.discountValue <= 0 ||
        (reqBody.discountValueMax !== undefined && reqBody.discountValueMax < 0)
      ) {
        throw new Error("Discount values must be positive.");
      }
      if (reqBody.minimumPurchaseValue < 0) {
        throw new Error("Minimum purchase value must be positive.");
      }
      if (
        reqBody.startDate &&
        reqBody.endDate &&
        reqBody.startDate >= reqBody.endDate
      ) {
        throw new Error("End date must be after start date.");
      }

      const response = await createDiscount({
        discountSlug: endpoint,
        data: reqBody,
      });
      if (vConsole) {
        vConsole.log("[Coupon Save] Response:", response);
      } else {
        console.log("[Coupon Save] Response:", response);
      }
      dispatch(resetFreeShipping());
      toast.success("Coupon created successfully");
      navigate(`/${storeType}/${storeId}/coupons`);
    } catch (error) {
      console.error("Create discount failed:", error);
      toast.error("Failed to create coupon");
    } finally {
      setLoading(false);
    }

    console.groupEnd();
  };
  return <SaveDiscountCouponSection loading={loading} onSave={handleSave} />;
};

export default SaveFreeShipping;
